import {RequisitionDisplay, RequisitionLine} from './_models'

export type RequisitionDownloadAttachment = {
  id?: number | null
  registryId?: number | null
  attachmentKey?: string | null
  fileName: string
  url: string
  viewUrl?: string | null
  downloadUrl?: string | null
  sizeBytes?: number | null
  available?: boolean | null
}

export type RequisitionDownloadDoc = {
  requisition: RequisitionDisplay
  lines: RequisitionLine[]
  attachments: RequisitionDownloadAttachment[]
}

const normalizeUrgencyLabel = (value: unknown): 'High' | 'Medium' | 'Low' => {
  const normalized = String(value || '').trim().toUpperCase()
  if (normalized === 'HIGH') return 'High'
  if (normalized === 'LOW') return 'Low'
  return 'Medium'
}

const resolveVesselName = (source: any): string => {
  const requisition = source?.requisition || source
  const candidates = [
    requisition?.vesselName,
    requisition?.vessel?.fleet_name,
    requisition?.vessel?.fleetName,
    requisition?.vessel?.name,
    requisition?.vessel?.vesselName,
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }
  return ''
}

export const buildRequisitionDownloadDoc = (requisition: any): RequisitionDownloadDoc => {
  const lines = Array.isArray(requisition?.lines) ? (requisition.lines as RequisitionLine[]) : []
  const attachments = Array.isArray(requisition?.attachments)
    ? requisition.attachments.map((attachment: any) => ({
        id: attachment?.id ?? null,
        registryId: attachment?.registryId ?? attachment?.id ?? null,
        attachmentKey: attachment?.attachmentKey ?? null,
        fileName: attachment?.fileName || '',
        url: attachment?.url || '',
        viewUrl: attachment?.viewUrl || null,
        downloadUrl: attachment?.downloadUrl || null,
        sizeBytes: attachment?.sizeBytes ?? null,
        available: attachment?.available ?? null,
      }))
    : []

  return {
    requisition: {
      id: Number(requisition?.id || 0),
      reqNumber: requisition?.requisition_code || requisition?.title || '-',
      title: requisition?.title || requisition?.name || '-',
      itemName: lines?.[0]?.manualItemName || lines?.[0]?.part?.name || '-',
      itemDescription: requisition?.description || '-',
      category: requisition?.category?.name || '-',
      urgency: normalizeUrgencyLabel(requisition?.type),
      requestedPort: requisition?.requestedPort?.name || requisition?.customPortName || '-',
      requiredDate: requisition?.requiredDeliveryDate || '-',
      quantity: String(
        lines.reduce((sum, line) => sum + Number(line?.quantity || 0), 0) ||
          requisition?.quantity ||
          ''
      ),
      estimatedValue: String(requisition?.estimatedUnitPrice || '-'),
      equipmentLink: requisition?.equipmentLink || '-',
      status: String(requisition?.status || 'Draft').replace(/_/g, ' '),
      rawStatus: requisition?.status || '',
      vesselStatus: '-',
      shoreStatus: '-',
      type: requisition?.type || '-',
      vesselName: resolveVesselName(requisition) || '-',
      requestFor: requisition?.requestFor || null,
      companyName: requisition?.category?.cgaid?.name || null,
      submittedAt: requisition?.submittedAt || null,
    },
    lines,
    attachments,
  }
}
